import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Truck, MapPin, Activity, Headphones, FileText, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';

/**
 * /demos — public gallery of live GoogLyn AI product demos.
 * Card grid follows the site's existing token set (white / #F5F7F5 sections,
 * black/90 headings, black/70 body, #E8A0B8 primary pink, existing shadcn
 * Card + Button primitives). No new colors, fonts, or styles introduced.
 */
const demos = [
  {
    title: 'GL End-To-End Drayage Operations',
    description:
      'End-to-end dashboard: quoting, dispatch, tracking, billing, and customer updates in one workspace.',
    href: 'https://demo.googlyn.ca/drayage-ops/',
    icon: Truck,
    testId: 'demo-card-drayage-ops',
  },
  {
    title: 'GL Customer Onboarding Tracker',
    description:
      'Real-time shipment tracking your customers can access directly.',
    href: 'https://demo.googlyn.ca/cascade-track/',
    icon: MapPin,
    testId: 'demo-card-customer-tracking',
  },
  {
    title: 'GL Pulse',
    description:
      'Real-time shipment tracking portal your customers can access anytime, anywhere.',
    href: '#',
    icon: Activity,
    testId: 'demo-card-gl-pulse',
  },
  {
    title: 'GL AI Concierge',
    description:
      'AI-native voice agent that handles customer interactions with elegance and intelligence.',
    href: '#',
    icon: Headphones,
    testId: 'demo-card-gl-ai-concierge',
  },
  {
    title: 'GL Invoice Automation',
    description:
      'Automates invoice matching, discrepancy flags, and reconciliation so finance teams close the books faster.',
    href: '#',
    icon: FileText,
    testId: 'demo-card-gl-invoice-automation',
  },
  {
    title: 'GL iAnalytics',
    description:
      'Reconciliation reports, revenue leakage detection, and detention control surfaced automatically so nothing slips through the cracks.',
    href: '#',
    icon: BarChart3,
    testId: 'demo-card-gl-ianalytics',
  },
];

const Demos = () => {
  return (
    <div className="w-full" data-testid="demos-page">
      {/* Page heading — mirrors IndustryPageHeading style/rhythm */}
      <section className="bg-white pt-16 md:pt-20 pb-6 md:pb-8" data-testid="demos-page-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl md:text-6xl font-medium text-black/95"
          >
            Live Demos
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-4 sm:mt-6 text-base sm:text-lg text-black/70 max-w-2xl mx-auto"
          >
            Explore working GoogLyn AI products, built for real operations.
          </motion.p>
        </div>
      </section>

      {/* Demo cards grid */}
      <section className="bg-[#F5F7F5] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
            data-testid="demos-grid"
          >
            {demos.map((demo, idx) => (
              <motion.div
                key={demo.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card
                  className="h-full bg-white border-[#F4C2D4]/30 hover:border-[#E8A0B8]/50 hover:-translate-y-1 transition-all duration-300"
                  style={{
                    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.06)',
                    borderRadius: '16px',
                  }}
                  data-testid={demo.testId}
                >
                  <CardHeader>
                    <div className="w-12 h-12 rounded-xl bg-[#E8A0B8]/10 flex items-center justify-center mb-4">
                      <demo.icon className="w-6 h-6 text-[#E8A0B8]" />
                    </div>
                    <CardTitle className="text-black/90 text-xl font-medium">
                      {demo.title}
                    </CardTitle>
                    <CardDescription className="text-black/70 text-sm sm:text-base leading-relaxed">
                      {demo.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <a
                      href={demo.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${demo.testId}-launch`}
                    >
                      <Button
                        type="button"
                        className="h-11 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105 px-5"
                      >
                        Launch Demo
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Demos;
